/**
 * Boot-time license gate. Renders its children only when the baked-in license
 * validates against the server. Unlicensed builds (no VITE_LICENSE_KEY) pass
 * through instantly, so stock/web deployments are unaffected.
 */
import React from 'react';
import {isLicensed, validateLicense, LicenseResult} from './license';

type State = {status: 'checking' | 'ok' | 'blocked'; result?: LicenseResult};

const centered: React.CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '0.75rem',
    padding: '2rem',
    textAlign: 'center',
    color: '#fbf1d4',
    background: '#282828',
    fontFamily: 'system-ui, sans-serif',
};

export const LicenseGate: React.FC<React.PropsWithChildren> = ({children}) => {
    // Unlicensed builds never gate — resolve to 'ok' synchronously.
    const [state, setState] = React.useState<State>(() =>
        isLicensed() ? {status: 'checking'} : {status: 'ok'}
    );

    React.useEffect(() => {
        if (!isLicensed()) {
            return;
        }
        let alive = true;
        validateLicense().then((result) => {
            if (!alive) {
                return;
            }
            setState({status: result.valid ? 'ok' : 'blocked', result});
        });
        return () => {
            alive = false;
        };
    }, []);

    if (state.status === 'ok') {
        return <>{children}</>;
    }

    if (state.status === 'checking') {
        return <div style={centered}>Checking license…</div>;
    }

    const reason = state.result?.reason || 'license invalid';
    const expiry = state.result?.expiresAt
        ? new Date(state.result.expiresAt).toLocaleString()
        : undefined;
    return (
        <div style={centered}>
            <h1 style={{margin: 0, fontSize: '1.5rem'}}>Access unavailable</h1>
            <p style={{margin: 0, opacity: 0.85}}>This application's license is {reason}.</p>
            {expiry && <p style={{margin: 0, opacity: 0.6}}>Expiry: {expiry}</p>}
            <p style={{margin: 0, opacity: 0.6}}>Please contact your administrator.</p>
        </div>
    );
};
